/**
 * Annual leave balance exceptions (owner's requirement, 2026-11, item 4).
 *
 *  - InsufficientBalanceNotice: shown in the leave form when the request is longer
 *    than the remaining balance, with a button to ask HR for an exception.
 *  - MyBalanceExceptions: the employee's own exception requests and their status.
 *  - BalanceExceptionsPanel: HR's queue - approve or reject (reason required to reject).
 *
 * The server decides everything: the shortfall, who may decide, and that an
 * approved exception is used once, by a leave request for the same dates.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { AlertTriangle, CheckCircle, Loader2, ShieldQuestion, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS = {
  pending: { cls: 'bg-amber-100 text-amber-800', en: 'Awaiting HR', ar: 'بانتظار الموارد البشرية' },
  approved: { cls: 'bg-green-100 text-green-800', en: 'Approved - submit your leave', ar: 'معتمد - قدّم طلب الإجازة' },
  rejected: { cls: 'bg-red-100 text-red-800', en: 'Rejected', ar: 'مرفوض' },
  used: { cls: 'bg-slate-100 text-slate-700', en: 'Used', ar: 'مستخدم' },
};

const day = (value) => {
  try { return format(new Date(value), 'dd MMM yyyy'); } catch { return value; }
};

const StatusBadge = ({ status, isRTL }) => {
  const s = STATUS[status] || STATUS.pending;
  return <Badge className={s.cls}>{isRTL ? s.ar : s.en}</Badge>;
};

export const InsufficientBalanceNotice = ({ shortfall, form, isRTL, onRequested }) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!shortfall) return null;

  const requestException = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/leave/balance-exceptions`, {
        leave_type_id: form.leave_type_id, start_date: form.start_date, end_date: form.end_date, reason: form.reason,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onRequested?.();
    } catch (e) {
      setError(e.response?.data?.detail?.error || (isRTL ? 'تعذّر رفع طلب الاستثناء' : 'The exception request could not be sent'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm space-y-2" role="alert" data-testid="insufficient-balance-notice">
      <p className="flex items-start gap-2 text-amber-900">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        {isRTL
          ? `رصيدك المتبقي ${shortfall.available_days} يوم لا يكفي لتغطية المدة المطلوبة (${shortfall.requested_days} يوم).`
          : `Your remaining balance of ${shortfall.available_days} days does not cover the ${shortfall.requested_days} days requested.`}
      </p>
      {shortfall.can_request_exception && (
        <>
          <p className="text-amber-800">
            {isRTL
              ? 'يمكنك رفع طلب استثناء لإدارة الموارد البشرية لهذه التواريخ. عند الموافقة عليه، قدّم طلب الإجازة نفسه مرة أخرى.'
              : 'You can ask HR for an exception for these dates. Once it is approved, submit the same leave request again.'}
          </p>
          <Button size="sm" variant="outline" disabled={busy} onClick={requestException} data-testid="request-balance-exception">
            {busy ? <Loader2 className="h-4 w-4 me-1 animate-spin" /> : <ShieldQuestion className="h-4 w-4 me-1" />}
            {isRTL ? 'رفع طلب استثناء' : 'Request an exception'}
          </Button>
        </>
      )}
      {error && <p className="text-red-700" data-testid="balance-exception-error">{error}</p>}
    </div>
  );
};

export const MyBalanceExceptions = ({ isRTL, refreshKey }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/leave/balance-exceptions/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [token, refreshKey]);
  if (!items.length) return null;
  return (
    <Card data-testid="my-balance-exceptions">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{isRTL ? 'طلبات استثناء الرصيد' : 'Balance exception requests'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 pb-2 text-sm">
            <span>{day(x.start_date)} → {day(x.end_date)} · {isRTL ? `${x.requested_days} يوم (ينقص ${x.shortfall_days})` : `${x.requested_days} days (${x.shortfall_days} short)`}</span>
            <span className="flex items-center gap-2">
              <StatusBadge status={x.status} isRTL={isRTL} />
              {x.decision_reason && <span className="text-xs text-slate-500">{x.decision_reason}</span>}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export const BalanceExceptionsPanel = ({ isRTL }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [decide, setDecide] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    axios.get(`${API_URL}/leave/balance-exceptions?status=pending`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/leave/balance-exceptions/${decide.item.id}/decision`,
        { action: decide.action, reason: reason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setDecide(null); load();
    } catch (e) {
      setError(e.response?.data?.detail?.error || (isRTL ? 'تعذّر حفظ القرار' : 'The decision could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  if (!items.length) return null;
  const rejecting = decide?.action === 'reject';
  return (
    <Card className="border-amber-200" data-testid="balance-exceptions-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{isRTL ? 'طلبات استثناء رصيد الإجازة السنوية' : 'Annual leave balance exceptions'}</CardTitle>
        <CardDescription>
          {isRTL ? 'عند الموافقة يُفعَّل الاستثناء في الإجازة السنوية للموظف لهذه التواريخ فقط.' : "Approving activates the exception on the employee's annual leave, for these dates only."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 pb-2 text-sm" data-testid={`balance-exception-${x.id}`}>
            <div className="min-w-0">
              <p className="font-medium">{(isRTL ? x.employee_name_ar : x.employee_name) || x.employee_name || x.employee_id}</p>
              <p className="text-slate-600">
                {day(x.start_date)} → {day(x.end_date)} · {isRTL
                  ? `مطلوب ${x.requested_days} · متاح ${x.available_days} · ينقص ${x.shortfall_days}`
                  : `requested ${x.requested_days} · available ${x.available_days} · short ${x.shortfall_days}`}
              </p>
              <p className="text-slate-500 break-words">{x.reason}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="text-green-700" onClick={() => { setDecide({ item: x, action: 'approve' }); setReason(''); setError(''); }}>
                <CheckCircle className="h-4 w-4 me-1" />{isRTL ? 'موافقة' : 'Approve'}
              </Button>
              <Button size="sm" variant="ghost" className="text-red-700" onClick={() => { setDecide({ item: x, action: 'reject' }); setReason(''); setError(''); }}>
                <XCircle className="h-4 w-4 me-1" />{isRTL ? 'رفض' : 'Reject'}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
      <Dialog open={!!decide} onOpenChange={(v) => !busy && !v && setDecide(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{rejecting ? (isRTL ? 'رفض الاستثناء' : 'Reject the exception') : (isRTL ? 'الموافقة على الاستثناء' : 'Approve the exception')}</DialogTitle>
          </DialogHeader>
          <Label htmlFor="balance-exception-reason">
            {rejecting ? (isRTL ? 'السبب (10 أحرف على الأقل) *' : 'Reason (at least 10 characters) *') : (isRTL ? 'ملاحظة (اختياري)' : 'Note (optional)')}
          </Label>
          <Textarea id="balance-exception-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setDecide(null)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button variant={rejecting ? 'destructive' : 'default'} disabled={busy || (rejecting && reason.trim().length < 10)} onClick={submit}>
              {rejecting ? (isRTL ? 'رفض' : 'Reject') : (isRTL ? 'موافقة' : 'Approve')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const TeamBalancesCard = ({ isRTL }) => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/leave/team/balances`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMembers(r.data.members || []))
      .catch(() => setMembers([]));
  }, [token]);
  if (!members.length) return null;
  return (
    <Card data-testid="team-balances-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{isRTL ? 'أرصدة الإجازة السنوية للفريق' : "Team's annual leave balances"}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead className="text-slate-500">
            <tr>
              <th className="text-start py-1">{isRTL ? 'الموظف' : 'Employee'}</th>
              <th className="text-start py-1">{isRTL ? 'المستحق' : 'Entitled'}</th>
              <th className="text-start py-1">{isRTL ? 'المستخدم' : 'Used'}</th>
              <th className="text-start py-1">{isRTL ? 'معلق' : 'Pending'}</th>
              <th className="text-start py-1">{isRTL ? 'المتبقي' : 'Remaining'}</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-t" data-testid={`team-balance-${m.id}`}>
                <td className="py-1">{(isRTL ? m.name_ar : m.name) || m.name}</td>
                <td className="py-1">{m.annual ? m.annual.allocated_days : '-'}</td>
                <td className="py-1">{m.annual ? m.annual.used_days : '-'}</td>
                <td className="py-1">{m.annual ? m.annual.pending_days : '-'}</td>
                <td className="py-1 font-medium">{m.annual ? m.annual.available_days : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
